import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Button, Input } from '@smartshell/ui-kit';

import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { EPaymentItemType } from 'types';

import { IPromoCodeFieldProps } from './intefaces';
import s from './PromoCodeField.module.scss';
import {
  EValidatePromoCodeItemsType,
  IValidatePromoCodeRequest,
  IValidatePromoCodeResponse,
  VALIDATE_PROMO_CODE,
} from './schema';
import { getErrorText } from './utils';

const Field: FC<IPromoCodeFieldProps> = ({
  value,
  onChange,
  disabled,
  clientUUID,
  items,
  setAvailableItems,
}) => {
  const { t } = useContext(LocalizationContext);

  const [code, setCode] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!error) {
      return;
    }
    setError(false);
  }, [code]);

  useEffect(() => {
    if (disabled) {
      setCode('');
      setError(false);
      onChange(null);
    } else if (!value) {
      setCode('');
      setError(false);
    }
  }, [value, disabled, onChange]);

  const prepareItemsData = useCallback(() => {
    if (!items || items.length === 0) {
      return undefined;
    }

    return items
      .map((item) => {
        let itemType: EValidatePromoCodeItemsType | undefined;

        switch (item.type) {
          case EPaymentItemType.GOOD:
            itemType = EValidatePromoCodeItemsType.GOOD;
            break;
          case EPaymentItemType.SERVICE:
            itemType = EValidatePromoCodeItemsType.SERVICE;
            break;
          case EPaymentItemType.TARIFF:
            itemType = EValidatePromoCodeItemsType.TARIFF;
            break;
        }

        return itemType ? { type: itemType, entity_id: item.id } : null;
      })
      .filter(Boolean) as {
      type: EValidatePromoCodeItemsType;
      entity_id: number;
    }[];
  }, [items]);

  const [validatePromoCode, { loading }] = useLazyQuery<
    IValidatePromoCodeResponse,
    IValidatePromoCodeRequest
  >(VALIDATE_PROMO_CODE, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setError(false);
      if (!data) {
        return;
      }
      const {
        promo_code: { id, value },
        items,
      } = data.validatePromoCode;
      onChange({ id, value });
      setCode(code);
      setAvailableItems && setAvailableItems(items);
    },
    onError: (error) => {
      setError(true);

      notification.info({
        t,
        type: 'error',
        title: t('promo.shop.error'),
        message: getErrorText(error?.message, t),
      });
    },
  });

  return (
    <div className={s['promo-code']}>
      <Input
        name="promoCode"
        title={t('promoCode')}
        value={code}
        onChange={setCode}
        error={error ? t('errors.promo.incorrect') : undefined}
        disabled={disabled || loading || Boolean(value)}
      />
      <Button
        disabled={disabled}
        isLoading={loading}
        variant={'secondary'}
        color={value ? 'red' : 'blue'}
        onClick={() => {
          if (value) {
            onChange(null);
            return;
          }
          validatePromoCode({
            variables: {
              input: {
                code,
                client_id: clientUUID || undefined,
                items: items ? prepareItemsData() : undefined,
              },
            },
          });
        }}
      >
        {value ? t('cancel') : t('apply')}
      </Button>
    </div>
  );
};

export const PromoCodeField: FC<IPromoCodeFieldProps> = (props) => (
  <FeatureHOC alias="discounts">
    <Field {...props} />
  </FeatureHOC>
);
